import {useCallback, useEffect, useMemo, useState} from 'react';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {SVG} from '../../../../../../../constants/assetLists';
import {
  createDefaultRegulation,
  FolderValues,
  isGeneralRegulationFolderId,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  RegulationFormValues,
} from '../../../types';
import RegulationsSidebar from './RegulationsSidebar';
import RegulationFormRow from './components/RegulationFormRow';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import {useDebounce} from '../../../../../../../hooks/useDebunce';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

function filterRegulationsForSelection(
  regulations: RegulationFormValues[],
  selection: string | null,
  regulationFolders: FolderValues[],
) {
  if (!selection) return [];

  if (isGeneralRegulationFolderId(selection, regulationFolders)) {
    return regulations.map((item, globalIndex) => ({item, globalIndex}));
  }

  return regulations
    .map((item, globalIndex) => ({item, globalIndex}))
    .filter(({item}) => item.folderId === selection);
}

function Regulations({selectedFieldPath}: Props) {
  const [selectedSidebarItem, setSelectedSidebarItem] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [listRevision, setListRevision] = useState(0);

  const debouncedSearch = useDebounce(searchQuery, 500);

  const {control, getValues, setValue} =
    useFormContext<NetworkDetailsFormValues>();

  const {append} = useFieldArray({
    control,
    name: `${selectedFieldPath}.regulations`,
    keyName: 'id',
  });

  const regulationsPath = `${selectedFieldPath}.regulations` as const;
  const regulationFolders =
    useWatch({control, name: `${selectedFieldPath}.regulationFolders`}) ?? [];
  const regulations = useWatch({control, name: regulationsPath}) ?? [];

  useEffect(() => {
    if (selectedSidebarItem) return;
    if (regulationFolders.length > 0) {
      setSelectedSidebarItem(regulationFolders[0].id);
    }
  }, [selectedFieldPath, regulationFolders, selectedSidebarItem]);

  useEffect(() => {
    setSearchQuery('');
  }, [selectedSidebarItem]);

  const getRegulationsCount = useCallback(
    (id: string) => {
      if (isGeneralRegulationFolderId(id, regulationFolders)) {
        return regulations.length;
      }

      return regulations.filter(item => item.folderId === id).length;
    },
    [regulations, regulationFolders],
  );

  const visibleRegulations = useMemo(() => {
    const folderFiltered = filterRegulationsForSelection(
      regulations,
      selectedSidebarItem,
      regulationFolders,
    );
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return folderFiltered;

    return folderFiltered.filter(({item}) =>
      item.name.toLowerCase().includes(query),
    );
  }, [regulations, selectedSidebarItem, debouncedSearch, regulationFolders]);

  const headerTitle = useMemo(() => {
    if (!selectedSidebarItem) return '';

    return (
      regulationFolders.find(f => f.id === selectedSidebarItem)?.name ?? ''
    );
  }, [selectedSidebarItem, regulationFolders]);

  const handleRemoveRegulation = useCallback(
    (globalIndex: number) => {
      const current = getValues(regulationsPath) ?? [];
      setValue(
        regulationsPath,
        current.filter((_, i) => i !== globalIndex),
        {shouldValidate: true, shouldDirty: true},
      );
    },
    [regulationsPath, getValues, setValue],
  );

  const handleAddRegulation = () => {
    if (!selectedSidebarItem) return;
    append(createDefaultRegulation({folderId: selectedSidebarItem}));
  };

  return (
    <div className="flex-1 min-h-0 flex gap-2">
      <RegulationsSidebar
        selectedFieldPath={selectedFieldPath}
        selectedItem={selectedSidebarItem}
        onSelectFolder={id => setSelectedSidebarItem(id)}
        getRegulationsCount={getRegulationsCount}
      />

      <div className="flex-1 min-h-0 p-3 border border-gray70 rounded-lg shadow-sm flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src={SVG.folderBlackIcon}
              alt="folder"
              className="w-5 h-4 object-contain"
            />
            <p className="m-0 text-xl font-bold text-darkBlue">{headerTitle}</p>
          </div>

          <AppTextInput
            placeholder="חיפוש רגולציה"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            containerClassName="!w-[499px]"
            className="bg-transparent !rounded-[28px] pl-16"
            endIcon={
              <div className="flex items-center gap-2">
                {searchQuery && (
                  <div
                    onClick={() => setSearchQuery('')}
                    className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer">
                    <img
                      src={SVG.closeDarkIcon}
                      alt="Clear"
                      className="w-[12px] h-[12px] object-contain"
                    />
                  </div>
                )}
                <div
                  className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                    searchQuery && 'bg-aqua'
                  }`}>
                  <img
                    src={SVG.searchIcon}
                    alt="Search"
                    className="w-[18px] h-[18px] object-contain"
                  />
                </div>
              </div>
            }
          />

          <p className="m-0 text-base text-black80">
            {visibleRegulations.length} רגולציות
          </p>
        </div>

        {/* List */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">
          {visibleRegulations.map(({item, globalIndex}, index) => (
            <RegulationFormRow
              key={`${item.id}-${listRevision}`}
              selectedFieldPath={selectedFieldPath}
              globalIndex={globalIndex}
              displayIndex={index + 1}
              selectedFolderId={selectedSidebarItem}
              onSaved={() => setListRevision(r => r + 1)}
              onRemove={handleRemoveRegulation}
            />
          ))}

          <div>
            <button
              type="button"
              className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
              disabled={!selectedSidebarItem}
              onClick={handleAddRegulation}>
              <img
                src={SVG.addDarkBlueIcon}
                alt="add"
                className="w-[14px] h-[14px] object-contain"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Regulations;
